/**
 * @fileOverview API level encryption manager. Keeps the server public key and
 * the post-login session, encrypts requests, decrypts responses and recovers
 * from ENC_* errors. Hooked into an axios instance with `attachEncryption`.
 * @module encryption/manager
 */

import axios from "axios";
import {
  API_PREFIX,
  ENC_VERSION,
  FLAG_RECHECK_MS,
  FLAG_RETRY_MS,
  LOGOUT_ROUTES,
  PINNED_FINGERPRINTS,
  SESSION_COOLDOWN_MS,
  SETTING_ROUTE,
  encLog,
  encState,
  isDev,
  isPlainPath,
  parseFlag,
} from "./config";
import {
  aesDecrypt,
  aesEncrypt,
  base64ToBytes,
  fingerprintHex,
  importAesKey,
  importRsaPublicKey,
  makeAad,
  newRid,
  normalizePath,
  randomBytes,
  rsaWrapKey,
} from "./crypto";
import { clearSession, getSession, saveSession } from "./sessionStore";

const KEY_RECOVERY_CODES = ["ENC_UNKNOWN_KEY_ID", "ENC_RSA_DECRYPT_FAILED"];
const BINARY_RESPONSE_TYPES = ["blob", "arraybuffer", "stream"];

const encError = (code) =>
  Object.assign(new Error("Encryption error"), { encCode: code });

const readHeader = (headers, name) =>
  headers?.get ? headers.get(name) : headers?.[name];

const apiBase = (fullUrl) =>
  `${new URL(fullUrl, window.location.origin).origin}${API_PREFIX}/`;

/** Identifies the current login so a session key is never reused across logins. */
const getOwner = () => {
  try {
    const user = JSON.parse(localStorage.getItem("bigfoot-web"))?.user;
    return `${user?.id ?? user?._id ?? user?.email ?? ""}|${user?.refreshToken ?? ""}`;
  } catch {
    return "|";
  }
};

const toMillis = (expiresAt) => {
  const value =
    typeof expiresAt === "number" ? expiresAt : Date.parse(expiresAt);
  return value < 1e12 ? value * 1000 : value;
};

const isEnvelope = (data) =>
  !!data &&
  typeof data === "object" &&
  data.v !== undefined &&
  !!data.iv &&
  !!data.ct &&
  !!data.tag;

const isEncCode = (code) => typeof code === "string" && code.startsWith("ENC_");

/* ------------------------------ encryption flag ----------------------------- */

let flagPromise = null;

const refreshFlag = (base) => {
  if (!flagPromise) {
    flagPromise = axios
      .get(`${base}general/getSetting`)
      .then((res) => {
        encState.enabled = parseFlag(res.data?.response?.is_encryption_enabled);
        encState.checkedAt = Date.now();
      })
      .catch(() => {
        encState.checkedAt = Date.now() - FLAG_RECHECK_MS + FLAG_RETRY_MS;
      })
      .finally(() => {
        flagPromise = null;
      });
  }
  return flagPromise;
};

const isEncryptionOn = async (base) => {
  if (encState.forcedOn) return true;
  if (Date.now() - encState.checkedAt >= FLAG_RECHECK_MS) {
    await refreshFlag(base);
  }
  return encState.enabled;
};

/* -------------------------------- public key -------------------------------- */

let publicKeyCache = null;
let publicKeyPromise = null;

const fetchPublicKey = async (base) => {
  const res = await axios.get(`${base}encryption/public-key`);
  const { kid, publicKey, fingerprint } = res.data.response;
  const spki = base64ToBytes(publicKey);
  const actual = await fingerprintHex(spki);

  if (actual !== String(fingerprint).toLowerCase()) {
    throw encError("ENC_KEY_FINGERPRINT_MISMATCH");
  }
  if (PINNED_FINGERPRINTS.length > 0) {
    if (!PINNED_FINGERPRINTS.includes(actual)) {
      throw encError("ENC_KEY_NOT_PINNED");
    }
  } else if (isDev) {
    console.warn(
      "[enc] REACT_APP_ENC_PINNED_FINGERPRINTS is empty, public key is not pinned",
    );
  }
  return { kid, key: await importRsaPublicKey(spki) };
};

const getPublicKey = (base) => {
  if (publicKeyCache) return Promise.resolve(publicKeyCache);
  if (!publicKeyPromise) {
    publicKeyPromise = fetchPublicKey(base)
      .then((value) => {
        publicKeyCache = value;
        return value;
      })
      .finally(() => {
        publicKeyPromise = null;
      });
  }
  return publicKeyPromise;
};

/* ---------------------------------- session ---------------------------------- */

let sessionPromise = null;
let sessionFailedAt = 0;

const establishSession = async (headers, base, owner) => {
  const sessionKeyRaw = randomBytes(32);
  let lastError;

  // A stale cached public key is refetched once.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { kid, key: rsaKey } = await getPublicKey(base);
      const encryptedKey = await rsaWrapKey(rsaKey, sessionKeyRaw);
      const res = await axios.post(
        `${base}encryption/session`,
        { kid, encryptedKey },
        {
          headers: {
            Authorization: readHeader(headers, "Authorization"),
            "X-Device-Id": readHeader(headers, "X-Device-Id"),
            "Content-Type": "application/json",
          },
        },
      );
      encLog("session", res.status, res.data);
      const { sessionId, expiresAt } = res.data.response;
      const session = {
        sessionId,
        key: await importAesKey(sessionKeyRaw),
        expiresAt: toMillis(expiresAt),
        owner,
      };
      saveSession(session);
      return session;
    } catch (error) {
      lastError = error;
      encLog("session failed", error?.response?.status, error?.response?.data);
      if (!KEY_RECOVERY_CODES.includes(error?.response?.data?.statusCode)) {
        break;
      }
      publicKeyCache = null;
    }
  }
  throw lastError;
};

const ensureSession = async (headers, base, owner) => {
  const existing = getSession(owner);
  if (existing) return existing;
  if (Date.now() - sessionFailedAt < SESSION_COOLDOWN_MS) {
    throw encError("ENC_SESSION_UNAVAILABLE");
  }
  if (!sessionPromise) {
    sessionPromise = establishSession(headers, base, owner)
      .catch((error) => {
        sessionFailedAt = Date.now();
        throw error;
      })
      .finally(() => {
        sessionPromise = null;
      });
  }
  return sessionPromise;
};

/* --------------------------------- requests --------------------------------- */

const isUnencryptable = (config) => {
  const data = config.data;
  return (
    BINARY_RESPONSE_TYPES.includes(config.responseType) ||
    String(readHeader(config.headers, "Content-Type") || "").includes(
      "multipart",
    ) ||
    (typeof FormData !== "undefined" && data instanceof FormData) ||
    (typeof Blob !== "undefined" && data instanceof Blob) ||
    data instanceof ArrayBuffer
  );
};

/**
 * Encrypts the request in place and stores the per-request context on
 * `config._enc` so the response can be decrypted with the same key.
 */
const prepareRequest = async (instance, config) => {
  if (!config._enc) config._enc = { original: config.data, ctx: null };
  config._enc.ctx = null;
  ["x-enc-sid", "x-enc-rid", "x-enc-ts"].forEach((name) =>
    config.headers?.delete?.(name),
  );

  const fullUrl = instance.getUri(config);
  const path = normalizePath(fullUrl);
  if (isPlainPath(path) || isUnencryptable(config)) return config;

  const base = apiBase(fullUrl);
  const method = (config.method || "get").toUpperCase();
  const authorization = readHeader(config.headers, "Authorization");
  const hasBody = config.data !== undefined && config.data !== null;
  if (!authorization && !hasBody) return config;
  if (!(await isEncryptionOn(base))) return config;

  const rid = newRid();
  const ts = Math.round(Date.now() + encState.clockOffsetMs);
  const plaintext = hasBody
    ? typeof config.data === "string"
      ? config.data
      : JSON.stringify(config.data)
    : "{}";

  if (!authorization) {
    // Pre-login: new AES key per request, wrapped with the server RSA key.
    const { kid, key: rsaKey } = await getPublicKey(base);
    const rawKey = randomBytes(32);
    const key = await importAesKey(rawKey);
    const aad = makeAad(null, rid, ts, method, path, "req");
    const parts = await aesEncrypt(key, plaintext, aad);
    const ek = await rsaWrapKey(rsaKey, rawKey);
    config.data = { v: ENC_VERSION, kid, rid, ts, ek, ...parts };
    config._enc.ctx = { key, rid, method, path };
  } else {
    // Post-login: AES session key established once per login.
    const session = await ensureSession(config.headers, base, getOwner());
    if (!hasBody && ["GET", "HEAD"].includes(method)) {
      config.headers.set("x-enc-sid", session.sessionId);
      config.headers.set("x-enc-rid", rid);
      config.headers.set("x-enc-ts", String(ts));
      config.data = undefined;
    } else {
      const aad = makeAad(session.sessionId, rid, ts, method, path, "req");
      const parts = await aesEncrypt(session.key, plaintext, aad);
      config.data = { v: ENC_VERSION, sid: session.sessionId, rid, ts, ...parts };
    }
    config._enc.ctx = { key: session.key, rid, method, path };
  }

  encLog("request", method, path, config.data ?? config.headers.toJSON?.());
  return config;
};

/* --------------------------------- responses --------------------------------- */

/** Replaces an encrypted response body with the decrypted object. */
const openResponse = async (response) => {
  const ctx = response?.config?._enc?.ctx;
  if (!ctx || !isEnvelope(response.data)) return response;
  encLog("response (raw)", response.status, response.data);
  const env = response.data;
  try {
    const aad = makeAad(env.sid, ctx.rid, env.ts, ctx.method, ctx.path, "res");
    response.data = JSON.parse(await aesDecrypt(ctx.key, env, aad));
  } catch {
    throw encError("ENC_AUTH_FAILED");
  }
  encLog("response (decrypted)", response.status, response.data);
  return response;
};

/** Applies the recovery for an ENC_* code, returns true when a retry makes sense. */
const recover = async (code, response) => {
  switch (code) {
    case "ENC_REQUIRED":
      encState.forcedOn = true;
      return true;
    case "ENC_UNKNOWN_KEY_ID":
    case "ENC_RSA_DECRYPT_FAILED":
      publicKeyCache = null;
      return true;
    case "ENC_STALE": {
      const date = Date.parse(
        readHeader(response.headers, "date") || response.headers?.date,
      );
      if (Number.isNaN(date)) return false;
      encState.clockOffsetMs = date - Date.now();
      return true;
    }
    case "ENC_REPLAY":
      return true;
    case "ENC_SESSION_INVALID":
    case "ENC_SESSION_EXPIRED":
    case "ENC_SESSION_REVOKED":
      clearSession();
      sessionFailedAt = 0;
      return true;
    default:
      return false;
  }
};

/** Re-runs the request once (new rid / ts / IV) when the ENC_* error is recoverable. */
const retryIfNeeded = async (instance, response) => {
  const config = response.config;
  const code = response.data?.statusCode;
  if (!isEncCode(code) || !config?._enc || config._encRetried) return null;
  if (!(await recover(code, response))) return null;
  config._encRetried = true;
  config.data = config._enc.original;
  return instance.request(config);
};

const afterResponse = (instance, response) => {
  const path = normalizePath(instance.getUri(response.config));

  if (path === SETTING_ROUTE) {
    const flag = response.data?.response?.is_encryption_enabled;
    if (flag !== undefined) {
      encState.enabled = parseFlag(flag);
      encState.checkedAt = Date.now();
    }
  }
  if (LOGOUT_ROUTES.includes(path)) clearSession();

  // Never surface ENC_* codes, fall back to the generic API error.
  if (isEncCode(response.data?.statusCode)) {
    response.data = { ...response.data, statusCode: 0 };
  }
};

/**
 * Adds request encryption / response decryption to an axios instance.
 * Call it before any other interceptor is registered so that the request is
 * encrypted last (after Authorization is set) and decrypted first (before the
 * 401 / 403 handling).
 */
export const attachEncryption = (instance) => {
  instance.interceptors.request.use(
    (config) => prepareRequest(instance, config),
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    async (response) => {
      await openResponse(response);
      const retried = await retryIfNeeded(instance, response);
      if (retried) return retried;
      afterResponse(instance, response);
      return response;
    },
    async (error) => {
      const response = error?.response;
      if (response) {
        try {
          await openResponse(response);
        } catch {
          // keep the raw body, the original error is still rejected below
        }
        const retried = await retryIfNeeded(instance, response);
        if (retried) return retried;
        afterResponse(instance, response);
      }
      return Promise.reject(error);
    },
  );

  return instance;
};
